'use client';

import React from 'react';
import { cn } from '@/lib/cn';

export type BeatLoaderVariant = 'accent' | 'white';

export interface BeatLoaderProps {
  className?: string;
  variant?: BeatLoaderVariant;
}

export function BeatLoader({ className, variant = 'accent' }: BeatLoaderProps) {
  const dot =
    variant === 'accent'
      ? {
          size: cn('h-3 w-3'),
          base: cn('bg-active'),
          active: cn('bg-accent'),
          x1: '1.25rem', // 20px = (12px dot + 8px gap)
          x2: '2.5rem', // 40px
        }
      : {
          size: cn('h-2 w-2'),
          base: cn('bg-primary/60'),
          active: cn('bg-primary'),
          x1: '1rem', // 16px = (8px dot + 8px gap)
          x2: '2rem', // 32px
        };

  return (
    <div
      className={cn('relative flex items-center gap-2', className)}
      aria-label="Loading"
      role="status"
      style={
        {
          // Used by the `oi-beat-forward` keyframes to move the active dot by the correct step size.
          '--oi-beat-x1': dot.x1,
          '--oi-beat-x2': dot.x2,
        } as React.CSSProperties
      }
    >
      <span className={cn('rounded-full', dot.size, dot.base)} />
      <span className={cn('rounded-full', dot.size, dot.base)} />
      <span className={cn('rounded-full', dot.size, dot.base)} />

      {/* Keep centering transform separate so animation doesn't override it */}
      <span className={cn('absolute left-0 top-1/2 -translate-y-1/2')}>
        <span
          className={cn('block rounded-full', dot.size, dot.active, 'animate-oi-beat-forward')}
        />
      </span>
    </div>
  );
}
